import { isEmpty, join, map, uniq } from 'lodash';
import { observer } from 'mobx-react';
import { useRootStore } from '@nandosaus/state-management';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { Box } from '../grid';

import { AccordionBlock } from '../accordion-block';
import { CareerOptionsBlock } from '../career-options-block';
import { ContentBlock } from '../content-block';
import { DeliveryOptionsMatrixBlock } from '../delivery-options-matrix-block';
import {
  ContentImageBlock,
  ContentImageCollageBlock,
  FeatureBlock,
  HeroBlock,
  HomeHeroBlock,
  PageHeaderBlock,
  ContentImageBlockHome,
} from '../content-image';
import { EnquiryFormBlock } from '../enquiry-form-block';
import { FeatureCardBlock } from '../feature-card-block';
import { IframeFormBlock } from '../iframe-form-block';
import { LinkCardBlock } from '../link-card-block';
import { MenuGridBlock } from '../menu-grid-block';
import { MosaicBlock } from '../mosaic-block';
import { PageListingBlock } from '../page-listing-block';
import { RelatedEntriesBlock } from '../related-entries-block';
import { ValuesBlock } from '../values-block';
import { VideoContentBlock } from '../video-content-block';

const CONTENT_MATRIX_MAPPING = {
  accordionBlock: AccordionBlock,
  careerOptionsBlock: CareerOptionsBlock,
  contentBlock: ContentBlock,
  contentImageBlock: ContentImageBlock,
  viSecondaryPromo: ContentImageBlockHome,
  contentImageCollageBlock: ContentImageCollageBlock,
  deliveryOptionsBlock: DeliveryOptionsMatrixBlock,
  enquiryFormBlock: EnquiryFormBlock,
  featureBlock: FeatureBlock,
  featureCardBlock: FeatureCardBlock,
  heroBlock: HeroBlock,
  viHomeHeroBlock: HomeHeroBlock,
  iframeFormBlock: IframeFormBlock,
  linkCardBlock: LinkCardBlock,
  menuCarouselBlock: MenuGridBlock,
  mosaicBlock: MosaicBlock,
  pageHeaderBlock: PageHeaderBlock,
  pageListingBlock: PageListingBlock,
  relatedEntriesBlock: RelatedEntriesBlock,
  valuesBlock: ValuesBlock,
  videoContentBlock: VideoContentBlock,
};

const Placeholder = () => <></>;

const StyledContentMatrix = styled(Box)`
  ${({ lastBlockBottomPadding }) =>
    lastBlockBottomPadding &&
    `
    > *:last-child {
      margin-bottom: 0 !important;
    }
  `}
`;

const ContentMatrix = ({ blocks, breadcrumbs, pageTitle, hasDarkBg, lastBlockBottomPadding, restaurant }) => {
  const { BrazeStore } = useRootStore();
  const router = useRouter();
  const isHome = router.pathname === '/';

  if (isEmpty(blocks)) {
    return null;
  }

  return (
    <StyledContentMatrix lastBlockBottomPadding={lastBlockBottomPadding}>
      {map(blocks, block => {
        const brazeMergedBlock = BrazeStore.getBrazeCmsContent(block);
        let Block = CONTENT_MATRIX_MAPPING[brazeMergedBlock.typeHandle];
        if (!Block) {
          Block = Placeholder;
        }
        return (
          <Block
            hasDarkBg={hasDarkBg}
            key={block.id}
            {...{ ...brazeMergedBlock, breadcrumbs, pageTitle, isHome, restaurant }}
          />
        );
      })}
    </StyledContentMatrix>
  );
};

ContentMatrix.propTypes = {
  blocks: PropTypes.arrayOf(
    PropTypes.shape({
      typeHandle: PropTypes.string.isRequired,
    })
  ),
  hasDarkBg: PropTypes.bool,
  breadcrumbs: PropTypes.shape({
    title: PropTypes.string,
    ancestors: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
      })
    ),
  }),
  pageTitle: PropTypes.string,
  lastBlockBottomPadding: PropTypes.bool,
  restaurant: PropTypes.shape({
    formattedDeliveryServices: PropTypes.object,
    orderingEnabled: PropTypes.bool,
  }),
};

ContentMatrix.defaultProps = {
  blocks: [],
  breadcrumbs: {},
  pageTitle: '',
  hasDarkBg: false,
  lastBlockBottomPadding: true,
  restaurant: null,
};

const buildContentMatrixElementQueryString = contentMatrixElements =>
  join(uniq(map(contentMatrixElements, contentMatrixElement => contentMatrixElement?.query)));

ContentMatrix.query = `
  contentMatrix {
    typeName: __typename
    ${buildContentMatrixElementQueryString(CONTENT_MATRIX_MAPPING)}
  }
`;
const ComposedContentMatrix = observer(ContentMatrix);
export { ComposedContentMatrix as ContentMatrix };
